/**
 * CEC-687. El motivo por el que el servidor rechazó una escritura de línea, listo para enseñarlo.
 *
 * El interceptor de errores ya traduce el cuerpo del backend a un mensaje en español y lo deja en
 * `error.error.message` —incluso cuando la respuesta viene con HTTP 200 y un `statusCode` de error
 * dentro—. Esta función elige la mejor cadena disponible y la pasa por `humanizeLineWriteMessage`:
 * sin ella, la pantalla enseñaba «[object Object]», nada, o jerga técnica en inglés.
 */
export function resolveLineWriteErrorMessage(error: unknown, fallback: string): string {
  const candidatos = [
    (error as any)?.error?.validationErrors?.[0]?.reason,
    (error as any)?.error?.message,
    (error as any)?.error?.error?.message,
    typeof (error as any)?.error === 'string' ? (error as any).error : null,
    (error as any)?.message,
  ];

  const mensaje = candidatos.find(
    (candidato) => typeof candidato === 'string' && candidato.trim().length > 0,
  );

  return mensaje ? humanizeLineWriteMessage(mensaje, fallback) : fallback;
}

/**
 * CEC-687. Motivos que el servidor devuelve hoy al guardar una línea, traducidos a lo que el comercial
 * necesita saber. Se reconocen por patrón porque varios llegan con los acentos rotos desde el backend
 * («v?lido para la compa??a»): el `.` de las expresiones cubre ese carácter. Solo están los que existen;
 * uno nuevo del servidor cae al texto de respaldo hasta que se añada aquí.
 */
const MOTIVOS_CONOCIDOS: ReadonlyArray<readonly [RegExp, string]> = [
  [
    /(Error converting value \{null\}|could not be converted to System\.Int32).*productId/i,
    'Esta línea no tiene producto asignado y por eso no se puede guardar. Asígnale un producto para poder editarla.',
  ],
  [/^No existe un producto con Id = \d+/, 'El producto elegido ya no existe. Elige otro producto para la línea.'],
  [
    /^El producto con Id = \d+ no es v.lido para la compa/,
    'El producto elegido no se puede usar en la compañía de esta cotización. Elige otro producto.',
  ],
  [/no son impuestos Selling activos/, 'El impuesto elegido no se puede aplicar en esta cotización. Elige otro impuesto.'],
  // CEC-689: el servidor ya no rechaza un impuesto por ser de otra compañía; rechaza los desactivados o que no
  // son de venta, y dos del mismo porcentaje en la misma línea. El de arriba se queda mientras quede un servidor
  // con el texto anterior.
  [/no son impuestos de venta activos/, 'El impuesto elegido está desactivado o no es de venta. Elige otro impuesto.'],
  [
    /dos impuestos del mismo porcentaje/,
    'Esta línea lleva dos impuestos del mismo porcentaje. Quita uno de los dos para poder guardar.',
  ],
  [/IDs de impuestos no existen/, 'El impuesto elegido ya no existe. Elige otro impuesto.'],
  [/impuestos no son v.lidos para el producto/, 'El impuesto elegido no se puede usar con este producto. Elige otro impuesto.'],
  [
    /impuestos no pertenecen a la compa/,
    'El impuesto elegido no pertenece a la compañía de esta cotización. Elige otro impuesto.',
  ],
  [/unidad de medida con Id = \d+ no est. activa/, 'La unidad de medida de la línea está desactivada. Elige otra unidad.'],
  [/unidad de medida con Id = \d+ no existe/, 'La unidad de medida de la línea ya no existe. Elige otra unidad.'],
  [
    /^La cotizaci.n con Id = \d+ no tiene una compa/,
    'Esta cotización no tiene compañía asignada y no se puede guardar. Avisa a soporte.',
  ],
  [/Se requiere un CompanyId/, 'No hay una compañía seleccionada. Elige una compañía y vuelve a intentarlo.'],
  [
    /^No existe una l.nea de (cotizaci.n|pedido)/,
    'Esta línea ya no existe; puede que otra persona la haya borrado. Recarga la página.',
  ],
  [
    /^No existe (una cotizaci.n|un pedido de venta) con Id/,
    'Esta cotización ya no existe; puede que otra persona la haya borrado. Recarga la página.',
  ],
  [
    /IsCost|AssociatedSaleLineId|IncludeSale|IncludeCost|debe ser una l.nea de venta/,
    'La línea de coste tiene que estar asociada a una línea de venta. Revisa la asociación.',
  ],
  [/^Opportunity with Id = \d+ not found/i, 'La oportunidad de esta cotización ya no existe. Avisa a soporte.'],
];

/**
 * Señales de que un texto no es para un comercial: jerga de .NET, ids internos, inglés, o los genéricos
 * del interceptor que no explican nada («Solicitud inválida», «Error interno del servidor»).
 */
const TEXTO_TECNICO =
  /System\.|Exception|Object reference|JSON|Path[ :]|stack|\bId = \d+|\b(the|with|not|found|failed|failure|response|occurred|invalid|value|request)\b|^Solicitud inv.lida$|^Error interno del servidor$|^Error inesperado \(/i;

/**
 * CEC-687. Lo que se enseña en el aviso, nunca el texto técnico del servidor: los motivos conocidos se
 * traducen, lo técnico o desconocido cae a `fallback`, y un motivo ya escrito en castellano llano pasa
 * tal cual. El original no se pierde: quien llama lo deja entero en la consola para soporte.
 */
export function humanizeLineWriteMessage(crudo: string, fallback: string): string {
  const texto = crudo.trim();
  const conocido = MOTIVOS_CONOCIDOS.find(([patron]) => patron.test(texto));

  if (conocido) {
    return conocido[1];
  }

  return TEXTO_TECNICO.test(texto) ? fallback : texto;
}

/** Rastro del último aviso mostrado, para no repetirlo. Lo guarda quien llama. */
export interface LineWriteErrorNotice {
  message?: string;
  at?: number;
}

/**
 * CEC-687. Decide si este fallo merece un aviso propio o es eco del anterior.
 *
 * Guardar la cotización lanza un PUT por línea a la vez. Cuando la causa es común —el producto
 * de varias líneas está archivado— llegan varios fallos con el mismo texto en décimas de segundo
 * y el usuario se comería una pila de avisos idénticos. Se muestra el primero y se callan sus
 * repeticiones durante la ventana; un mensaje distinto siempre pasa.
 */
export function shouldNotifyLineWriteError(
  previo: LineWriteErrorNotice,
  message: string,
  now: number,
  windowMs = 2000,
): { notify: boolean; notice: LineWriteErrorNotice } {
  const esEco =
    previo.message === message &&
    typeof previo.at === 'number' &&
    now - previo.at < windowMs;

  return esEco
    ? { notify: false, notice: previo }
    : { notify: true, notice: { message, at: now } };
}
